import brandData from "@brand/theme.json";

/**
 * Legal / tax configuration + helpers for the customer SPA.
 *
 * ⚠️ SCAFFOLDING — THIS IS NOT LEGAL OR TAX ADVICE.
 *
 * The values are PLACEHOLDERS read from `brand/theme.json`. Before go-live the
 * operator MUST:
 *   1. Confirm the VAT rate + treatment with their accountant. `ratePercent: 23`
 *      is the Irish STANDARD rate placeholder — reduced or zero rates may apply.
 *      Displayed prices are always VAT-INCLUSIVE (checkout charges exactly the
 *      price shown; see the HFE-1 note in the COMP-2 ADR).
 *   2. Set `vat.confirmed = true` in brand/theme.json once verified. Until then
 *      the on-page disclosure honestly flags the rate as an unconfirmed
 *      placeholder so nobody mistakes scaffolding for a real tax claim.
 *   3. Fill in `business.legalName / address / vatNumber` (rendered in the
 *      footer). Leaving them blank shows a visible "operator must supply" note.
 *
 * To change any of this the operator edits `brand/theme.json` — no code change.
 */

export interface VatConfig {
  /** VAT rate as a percentage, e.g. 23 for 23%. */
  ratePercent: number;
  /** Operator flips this to true once the rate + treatment are confirmed. */
  confirmed: boolean;
}

export interface BusinessIdentity {
  legalName: string;
  address: string;
  vatNumber: string;
}

export const vatConfig: VatConfig = {
  ratePercent: brandData.vat.ratePercent,
  confirmed: brandData.vat.confirmed,
};

export const businessIdentity: BusinessIdentity = {
  legalName: brandData.business.legalName,
  address: brandData.business.address,
  vatNumber: brandData.business.vatNumber,
};

/**
 * Split a VAT-INCLUSIVE total into its net + VAT parts.
 *
 * For a total that already includes VAT at rate r%, the VAT component is
 * `total - total / (1 + r/100)`. We round the NET to the nearest cent and
 * derive VAT as `total - net` so the two parts always sum back to the exact
 * input total (no rounding drift). A non-positive rate yields zero VAT.
 */
export function vatBreakdown(
  totalCents: number,
  ratePercent: number,
): { netCents: number; vatCents: number } {
  if (ratePercent <= 0) return { netCents: totalCents, vatCents: 0 };
  const netCents = Math.round(totalCents / (1 + ratePercent / 100));
  return { netCents, vatCents: totalCents - netCents };
}
